import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { SmsSentMessagesService, SummaryService, TicketsService } from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import { Constants } from '@shared/models';
import { MyTimestampPipe } from '@shared/pipes'
import type {
    TSmsSentMessage, TCustomer, TResponse, TSmsSentStatus,
    TSummary, TTimestamp, TRoutesViewModel
} from '@shared/models';

@Component({
    'selector': 'app-route7Notification',
    'templateUrl': './route7Notification.page.html',
    'styleUrls': ['./route7Notification.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule, MyTimestampPipe ]
})
export class Route7NotificationPage extends ViewBasePage implements OnInit, OnDestroy {
    public vm: TRoutesViewModel;
    private subscription: any;

    constructor(private ticketsService: TicketsService, 
        private smsSentMessagesService: SmsSentMessagesService, private summaryService: SummaryService,
        injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_ROUTE_PAGE7);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        this.vm = super.getViewModel();

        // This page is only for dropoffs.  If no dropoffs, then exit early.
        this.vm.numDropoffPassengers = this.vm.dropoffManifest.selectedTickets.length;
        if (this.vm.numDropoffPassengers === 0) {
            this.vm.step7Complete = true;
            this.onNextClick();
        }

        // Show the message being sent to John Doe.
        const settings = await this.settingsService.get();
        const allAboardSetting = settings.allAboard;
        const rawMessage = allAboardSetting.rawMessage;
        const customer: TCustomer = {
            'customerName': 'John Doe',
            'cellPhone': '',
            'shuttleDates': [],
            'uid': ''
        };

        // Do the variable replacement.
        await this.variableReplacementService.reset();
        this.variableReplacementService.customer = customer;
        const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);
        this.vm.sms.allAboardRenderedMessage = renderedMessage;
        this.vm.driverName = this.sessionService.getUsersFullName;

        //
        this.initSentStatuses();
        this.initMoreMenu();

        this.vm.sessionId = await this.getRouteCount();
        const observable = this.smsSentMessagesService.getObservable('all');
        this.subscription = observable.subscribe((messages: TSmsSentMessage[]) => {
            // We are only interested in the messages that have been sent from this session.
            const myMessages = messages.filter((msg: TSmsSentMessage) => {
                if (!msg.sessionId) {
                    return false;
                }
                if (this.vm.sessionId === msg.sessionId) {
                    return msg;
                }
                return false;
            });

            if (myMessages.length === 0) {
                return false;
            }

            return true;

            // myMessages.forEach((item) => {
            //     const status = this.vm.sentStatuses.find((item2) => {
            //         // console.log('item.toNumber   = ', item.toNumber);
            //         // console.log('item2.cellPhone = ', item2.cellPhone);
            //         const cellPhoneWithoutCountryCode = item.toNumber.substring(item.countryCode.length);
            //         return (cellPhoneWithoutCountryCode === item2.cellPhone);
            //     });
            //     if (status) {
            //         // status.sentDate = this.dateService.formatTimestamp('now', Constants.DateTimeFormats.Full);
            //         status.sentDate = this.dateService.now();
            //         status.sentStatus = 'pending';
            //     }
            // });
            // return true;
        });
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.subscription?.unsubscribe();
    }

    public initMoreMenu(): void {
        this.pushPopoverMenuItem('Show Tour', 'show-route-7-tour');
    }

    private async getRouteCount(): Promise<number> {
        try {
            const overallSummary: TSummary = await this.summaryService.getOverallSummary();
            const routeCount = overallSummary.numRoutes ?? 0;
            return routeCount;
        } catch (err) {
            throw new Error('Error getting route count.');
        }
    }

    private initSentStatuses() {
        let sequenceNo = 0;
        this.vm.sentStatuses = [];

        for (const ticket of this.vm.dropoffManifest.selectedTickets) {
            //            if (ticket.fromDealership) {
            ++sequenceNo;
            ticket.allAboardDate = this.dateService.getMinimumDate();

            const cellPhoneMasked = this.utilsService.maskPhoneNumber(ticket.cellPhone);
            const status: TSmsSentStatus = {
                'sequenceNo': sequenceNo,
                'ticket': ticket,
                'passengerName': ticket.passengerName,
                'maskedCellPhone': cellPhoneMasked,
                'sentStatus': 'pending',
                'sentDate': ''
            };
            this.vm.sentStatuses.push(status);
        }
    }

    public greetMessage(): string {
        const customerz = (this.vm.numDropoffPassengers === 1) ? 'customer' : 'customers';
        const minutez = (this.vm.setting.allAboard.numMinutes === 1) ? 'minute' : 'minutes';

        const msg = `${this.vm.setting.allAboard.numMinutes} ${minutez}, ` +
            `after the message has been sent, expect and greet ` +
            `${this.vm.numDropoffPassengers} ${customerz} at the shuttle station.`;

        return msg;
    }

    public async onNextClick() {
        super.setViewModel(this.vm);

        if (this.vm.step7Complete || this.vm.numDropoffPassengers === 0) {
            this.router.navigateByUrl('/routes/route8Congratulations');
        } else if (this.vm.numDropoffPassengers > 0) {
            const msg = 'The sms text message has not been sent.  Are you sure you want to skip this step?';
            const response = await this.uxHelperService.showAlert('Confirmation', msg);
            if (response.okay) {
                this.router.navigateByUrl('/routes/route8Congratulations');
            }
        }
    }

    public async onSendSmsAllAboardClick() {
        try {
            await this.uxHelperService.showSpinner('Sending text messages.');

            if (!this.sessionService.getAccountProfile().featuresInfo.campaign) {
                this.uxHelperService.showAlert('Sorry.', Constants.FEATURE_UPGRADE_TO_CUSTOMER_PACKAGE);
                this.vm.step7Complete = true;
                return;
            }

            for (const status of this.vm.sentStatuses) {
                const cellPhoneUnformatted = this.utilsService.unformatPhoneNumber(status.ticket.cellPhone);
                const customer: TCustomer = {
                    'customerName': status.ticket.passengerName,
                    'cellPhone': cellPhoneUnformatted,
                    'sessionId': this.vm.sessionId,
                    'shuttleDates': [],
                    'uid': '',
                };
                // const data = { 'customers': [customer], 'debug': '**' + status.sequenceNo + '**' };
                const data = { 'customers': [customer] };
                const response = await this.smsService.onAllAboardEvent(data);
                // console.log({ response });

                if (response) {
                    // Keep track of when we sent out the all aboard message.
                    const now = this.dateService.now();
                    status.ticket.allAboardDate = now;
                    status.sentStatus = 'sent';
                    await this.ticketsService.update(status.ticket);
                    // console.log('status:', status);

                    // const stat = this.sentStatuses.find(o => o.sequenceNo === status.sequenceNo);
                    // status.sentDate = this.dateService.formatTimestamp(now, Constants.DateTimeFormats.MilliTime);
                    // status.sentDate = this.dateService.now();
                }
            }
            // console.log('this.vm.sentStatuses:', this.vm.sentStatuses);

            this.vm.step7Complete = true;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }
}
